import React from "react";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import {
    JsonSchema,
    JsonSchemaInteger,
    JsonSchemaNumber,
    JsonSchemaString,
} from "./types";
import { STRING_FORMAT_OPTIONS, TYPE_OPTIONS } from "./constants";

interface FieldEditorProps<T extends JsonSchema = JsonSchema> {
    schema: T;
    onUpdate: (updates: Partial<T>) => void;
    disabled?: boolean;
}

export function StringFieldsEditor({
    schema,
    onUpdate,
    disabled = false,
}: FieldEditorProps<JsonSchemaString>) {
    return (
        <>
            <AcxSelectSingle
                id="detail-format"
                options={STRING_FORMAT_OPTIONS}
                valueField="value"
                labelField="label"
                defaultValue={
                    STRING_FORMAT_OPTIONS.find(
                        (opt) => opt.value === (schema.format || ""),
                    ) || STRING_FORMAT_OPTIONS[0]
                }
                onChange={(selected) => {
                    if (selected) {
                        onUpdate({
                            format: selected.value || undefined,
                        });
                    }
                }}
                inputLabel="Format"
                fullWidth
                isDisabled={disabled}
            />
            <AcxMainTextField
                id="detail-pattern"
                labelText="Pattern (Regex)"
                placeholderText="e.g., ^[a-z]+$"
                value={schema.pattern || ""}
                onChange={(e) =>
                    onUpdate({
                        pattern: e.target.value || undefined,
                    })
                }
                fullWidth
                isDisabled={disabled}
            />
            <AcxMainTextField
                id="detail-minLength"
                labelText="Min Length"
                type="number"
                value={schema.minLength ?? ""}
                onChange={(e) =>
                    onUpdate({
                        minLength: e.target.value
                            ? parseInt(e.target.value)
                            : undefined,
                    })
                }
                fullWidth
                isDisabled={disabled}
            />
            <AcxMainTextField
                id="detail-maxLength"
                labelText="Max Length"
                type="number"
                value={schema.maxLength ?? ""}
                onChange={(e) =>
                    onUpdate({
                        maxLength: e.target.value
                            ? parseInt(e.target.value)
                            : undefined,
                    })
                }
                fullWidth
                isDisabled={disabled}
            />
        </>
    );
}

export function NumberIntegerFieldsEditor({
    schema,
    onUpdate,
    disabled = false,
}: FieldEditorProps<JsonSchemaNumber | JsonSchemaInteger>) {
    return (
        <>
            <AcxMainTextField
                id="detail-minimum"
                labelText="Minimum"
                type="number"
                value={schema.minimum ?? ""}
                onChange={(e) =>
                    onUpdate({
                        minimum: e.target.value
                            ? parseFloat(e.target.value)
                            : undefined,
                    })
                }
                fullWidth
                isDisabled={disabled}
            />
            <AcxMainTextField
                id="detail-maximum"
                labelText="Maximum"
                type="number"
                value={schema.maximum ?? ""}
                onChange={(e) =>
                    onUpdate({
                        maximum: e.target.value
                            ? parseFloat(e.target.value)
                            : undefined,
                    })
                }
                fullWidth
                isDisabled={disabled}
            />
        </>
    );
}

export function TypeSelectorField({
    schema,
    onUpdate,
    disabled = false,
}: FieldEditorProps) {
    return (
        <AcxSelectSingle
            id="type-selector"
            options={TYPE_OPTIONS}
            valueField="value"
            labelField="label"
            defaultValue={TYPE_OPTIONS.find((opt) => opt.value === schema.type)}
            onChange={(selected) => {
                if (selected) {
                    onUpdate({ type: selected.value as any });
                }
            }}
            inputLabel="Type"
            fullWidth
            isDisabled={disabled}
        />
    );
}
